// Components==============
import updateExerciseStatus from 'actions/authentication/updateExerciseStatus';
import ControlBar from 'components/Layout/ControlBar/ControlBar';
import SoundBar from 'components/Layout/SoundBar/SoundBar';
import ExerciseBottom from 'components/UI/ExerciseBottom/ExerciseBottom';
import ExerciseWrapper from 'components/UI/ExerciseWrapper/ExerciseWrapper';
import { SoundType } from 'components/UI/LetterBlock/LetterBlock';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { DragDropContext, DropResult } from 'react-beautiful-dnd';
import { useDispatch, useSelector } from 'react-redux';
import { HomeworkContext } from 'screens/Homework/Homework';
import { useHomeworkNavigation } from 'utilities/useHomeworkNavigation';
import Category from './components/Category/Category';
import { defaultGroupTileBarStateValue } from './components/Group/Group';
import History from './components/History/History';
import Overview from './components/Overview/Overview';
import './SoundGroupExercise.module.scss';
// =========================

export type UserAnswerType = {
    id: number;
    bars: null | string[][];
    text: string;
    correctBars: string[][];
    correctText: string;
};

type SoundGroupExerciseContextTypes = {
    exerciseStatus: exerciseStatusType;
    setExerciseStatus: React.Dispatch<React.SetStateAction<exerciseStatusType>>;
    userAnswers: UserAnswerType[];
    setUserAnswers: React.Dispatch<React.SetStateAction<UserAnswerType[]>>;
    givenAnswers: UserAnswerType[] | undefined;
    userBarAnswer: string[][] | null;
    setUserBarAnswer: React.Dispatch<React.SetStateAction<string[][] | null>>;
    current: number;
    setCurrent: React.Dispatch<React.SetStateAction<number>>;
    answer: WordsWithImages;
    words: WordsWithImages[];
    dragState: null | DropResult;
    dragStartId: string;
    toggleIsUpdatedOnClick: boolean;
    setUserIsTypingAnswer: React.Dispatch<React.SetStateAction<boolean>>;
    displayHistory: boolean;
    setDisplayHistory: React.Dispatch<React.SetStateAction<boolean>>;
    dragging: boolean;
    useImages: 0 | 1;
    useWords: 0 | 1;
    useInput: 0 | 1;
    activeTileBar: string;
    setActiveTileBar: React.Dispatch<React.SetStateAction<string>>;
    activeTileBarState: SoundType[];
    setActiveTileBarState: React.Dispatch<React.SetStateAction<SoundType[]>>;
    currentAnswer: SoundType[][];
    setCurrentAnswer: React.Dispatch<React.SetStateAction<SoundType[][]>>;
};

export const SoundGroupExerciseContext = createContext(
    {} as SoundGroupExerciseContextTypes
);

export default function SoundGroupExercise({
    exercise,
    setStatus,
}: {
    exercise: ExerciseEntity;
    setStatus: React.Dispatch<React.SetStateAction<exerciseStatusType>>;
}) {
    const [words, setWords] = useState(exercise?.words);
    const useImages = exercise?.use_images;
    const useWords = exercise?.use_words;
    const useInput = exercise?.answer_text;

    const dispatch = useDispatch();
    const { setProgress } = useContext(HomeworkContext);

    const [exerciseStatus, setExerciseStatus] = useState<exerciseStatusType>(
        'started'
    );
    const [userAnswers, setUserAnswers] = useState<UserAnswerType[]>([]);
    const { next, current, setCurrent } = useHomeworkNavigation(userAnswers);
    const [answer, setAnswer] = useState(words[current]);
    const [dragState, setDragState] = useState<null | DropResult>(null);
    const [dragStartId, setDragStartId] = useState('');

    const [activeTileBar, setActiveTileBar] = useState('initial');
    const [activeTileBarState, setActiveTileBarState] = useState<SoundType[]>(
        defaultGroupTileBarStateValue
    );
    const [userBarAnswer, setUserBarAnswer] = useState<null | string[][]>(null);

    const [toggleIsUpdatedOnClick, setToggleIsUpdatedOnClick] = useState(false);
    const [userIsTypingAnswer, setUserIsTypingAnswer] = useState(false);

    const [currentAnswer, setCurrentAnswer] = useState<SoundType[][]>([[]]);
    const [displayHistory, setDisplayHistory] = useState(false);

    const givenAnswers = useSelector((state: ReduxStore) =>
        state?.authentication?.user?.exercise_status?.find(
            (e) =>
                e.exercise_id === exercise?.id &&
                e.exercise_type === 'App\\Models\\Exercise'
        )
    )?.metadata as UserAnswerType[] | undefined;

    const [dragging, setDragging] = useState(false);

    useEffect(() => {
        if (exerciseStatus === 'started' && givenAnswers) {
            // get all answered words
            const answeredWords = givenAnswers
                ?.map((a) => words.find((word) => word.id === a.id))
                .filter((exists) => exists) as WordsWithImages[];

            // get all words except the answered ones
            const filteredWords = words.filter(
                (word) => !givenAnswers.find((a) => a.id === word.id)
            );

            // put answered words before filtered words to only get unanswered words
            setWords([...answeredWords, ...filteredWords]);
            setUserAnswers(givenAnswers);
            setCurrent(givenAnswers.length);
        }
    }, [exerciseStatus]);

    useEffect(() => {
        if (useInput) {
            if (current === words.length) setExerciseStatus('finished');
        } else if (userAnswers.length === words.length)
            setExerciseStatus('finished');
    }, [current, userAnswers]);

    useEffect(() => setAnswer(words[current]), [words, current]);

    useEffect(() => console.log(answer?.klankgroepen), [answer]);

    useEffect(() => {
        const emptyAnswers = Array(answer?.klankgroepen?.length).fill(
            defaultGroupTileBarStateValue
        ) as [SoundType[]];

        setDisplayHistory(false);
        setCurrentAnswer(emptyAnswers);
    }, [answer]);

    useEffect(() => setStatus(exerciseStatus), [exerciseStatus]);

    useEffect(() => {
        setProgress(userAnswers?.length);
        if (givenAnswers && givenAnswers.length >= userAnswers.length) return;

        // save user answer
        if (userAnswers?.[current]?.id)
            dispatch(
                updateExerciseStatus({
                    exercise_id: exercise?.id,
                    exercise_type: 'homework',
                    finished: 0,
                    newWord: userAnswers[current],
                })
            );
    }, [userAnswers]);

    return (
        <SoundGroupExerciseContext.Provider
            value={{
                exerciseStatus,
                setExerciseStatus,
                userAnswers,
                setUserAnswers,
                givenAnswers,
                current,
                setCurrent,
                userBarAnswer,
                setUserBarAnswer,
                answer,
                words,
                dragState,
                dragStartId,
                useImages,
                useWords,
                useInput,
                toggleIsUpdatedOnClick,
                setUserIsTypingAnswer,
                dragging,
                displayHistory,
                setDisplayHistory,
                activeTileBar,
                setActiveTileBar,
                setActiveTileBarState,
                activeTileBarState,
                currentAnswer,
                setCurrentAnswer,
            }}
        >
            <DragDropContext
                onDragEnd={(result) => {
                    setDragState(result);
                    setDragging(false);
                }}
                onDragStart={(result) => {
                    setDragStartId(result?.draggableId);
                    setDragging(true);
                }}
            >
                <ExerciseWrapper padding="7em 0">
                    <div styleName="wrapper">
                        {exerciseStatus === 'finished' && useInput ? (
                            <Overview />
                        ) : (
                            <Category word={answer} index={0} />
                        )}
                    </div>
                </ExerciseWrapper>
                {exerciseStatus === 'started' && <History />}
                <ExerciseBottom
                    controlBar={
                        <ControlBar
                            next={next}
                            phoneme={answer?.phoneme}
                            exerciseStatus={exerciseStatus}
                            preventAutoPlay={
                                !!(useImages && answer?.images?.length) ||
                                !!(useWords && answer?.pivot?.string)
                            }
                            textToBeRead={answer?.name}
                            audioToBePlayed={answer?.audio}
                            canType={useInput !== 0}
                        />
                    }
                    soundBar={
                        <SoundBar
                            activeTileBarState={activeTileBarState}
                            setActiveTileBarState={setActiveTileBarState}
                            setToggleIsUpdatedOnClick={
                                setToggleIsUpdatedOnClick
                            }
                            userIsTypingAnswer={userIsTypingAnswer}
                        />
                    }
                />
            </DragDropContext>
        </SoundGroupExerciseContext.Provider>
    );
}
